/**
 * zoneSnapUtils — Snap an bestehende Höhenzonen (#2, Terrain-Epic #623).
 *
 * Beim Zeichnen von Ebenen/Böschungen sollen neue Punkte an bereits gesetzte
 * Zonen-Eckpunkte und -Kanten einrasten. Das ist im neuen Modell praktisch
 * Pflicht: Böschung und Ebene müssen sauber aneinanderstoßen (~10 cm), damit
 * die 3D-Interpolation sie als benachbart erkennt (scene3d/terrain.js).
 *
 * Reine Geometrie (keine Fabric-/React-Imports), damit unit-testbar.
 */

/** Nächster Punkt auf der Strecke a–b zu p (mit Endpunkt-Clamping). */
export function closestPointOnSegment(p, a, b) {
  const abx = b.x - a.x;
  const aby = b.y - a.y;
  const lenSq = abx * abx + aby * aby;
  if (lenSq === 0) return { x: a.x, y: a.y };
  let t = ((p.x - a.x) * abx + (p.y - a.y) * aby) / lenSq;
  t = Math.min(1, Math.max(0, t));
  return { x: a.x + t * abx, y: a.y + t * aby };
}

const dist = (a, b) => Math.hypot(a.x - b.x, a.y - b.y);

/**
 * Rastet `point` an die Vertices/Kanten bestehender Zonen ein.
 * Vertices haben Vorrang vor Kanten (exakte Ecktreffer). Gibt den nächsten
 * Treffer innerhalb `threshold` zurück, sonst den Originalpunkt.
 *
 * @param {{x:number,y:number}} point
 * @param {Array<{vertices:Array<{x:number,y:number}>}>} zones
 * @param {number} [threshold=10] - Snap-Radius in px (= cm)
 * @param {string|null} [excludeId=null] - Zone-ID, deren Punkte ignoriert werden (z. B. die gerade bearbeitete)
 * @returns {{x:number, y:number, snapped:boolean, type:('vertex'|'edge'|null)}}
 */
export function snapPointToZones(point, zones, threshold = 10, excludeId = null) {
  let best = { x: point.x, y: point.y, snapped: false, type: null };
  let bestDist = threshold;

  // 1) Vertices (Vorrang)
  for (const zone of zones || []) {
    if (!zone || zone.id === excludeId || !Array.isArray(zone.vertices)) continue;
    for (const v of zone.vertices) {
      const d = dist(point, v);
      if (d <= bestDist) {
        bestDist = d;
        best = { x: v.x, y: v.y, snapped: true, type: 'vertex' };
      }
    }
  }
  if (best.type === 'vertex') return best;

  // 2) Kanten (nur wenn kein Vertex getroffen wurde)
  for (const zone of zones || []) {
    if (!zone || zone.id === excludeId || !Array.isArray(zone.vertices)) continue;
    const vs = zone.vertices;
    for (let i = 0; i < vs.length; i++) {
      const a = vs[i];
      const b = vs[(i + 1) % vs.length];
      const q = closestPointOnSegment(point, a, b);
      const d = dist(point, q);
      if (d <= bestDist) {
        bestDist = d;
        best = { x: q.x, y: q.y, snapped: true, type: 'edge' };
      }
    }
  }

  return best;
}
